import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { getTutorials } from "../../../store/tutorials";
import { getReportDetails, setLastPhotoNumber } from "../../../store/reportDetails";
import { getCoverData } from "../../../store/coverData";
import { getCurrentUser } from "../../../services/authService";
import { DEFAULT_REPORT_TITLE } from "../../../utils/constants";
import { CustomUTMOverview, FVIPipingItem, PDFImageProps, User, UTMInspection } from "../../../utils/models";
import { tutorialConstants } from '../../../utils/constants';
import { reArrangePhotoRecord, validateWorkOrderNumber } from "../../../utils/utilities";
import { useAppDispatch, useAppSelector } from "../../../store/configureStore";

import LRUTGeneralInformationForm, { LRUTGeneralInformation } from "../../forms/lrut/lrutGeneralInformation";

import Modal from "../../themed/modal";
import Button from "../../themed/button";
import Elevation from "../../themed/instruction-modals/elevation";
import LoadingBadge from "../../themed/loading-badge";
import CustomLRUTReport from ".";
import VIPhotoRecordForm from "../../forms/visual-inspection/viPhotoRecordForm";
import Conditional from "../../common/conditional";
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomUTMSection from "../custom-utm/custom-utm-section";
import Drawing from "../../forms/custom-lpt/drawing";
import ExecutiveSummaryForm from "../../forms/visual-inspection/executiveSummaryForm";
import FVICoverData from "../../themed/fvi-cover-data";
import LRUTDataItems, { LRUTDataItem } from "./lrutDataItems";
import PipingItems from "../custom-visual-inspection/piping-items";
import ReportTitleForm from "../../forms/custom-fvi/reportTitleForm";
import SyncTime from "../../blocks/sync-time";
import Zippy from "../../themed/zippy";
import WarningNotice from "../custom-visual-inspection/warning-notice";

import useAutoSaveReport from "../../../hooks/useAutoSave";
import useProjectDetails from "../../../hooks/useProjectDetails";

import config from "../../../utils/config";
import toastService from "../../../services/toastService";
import useAnomalyRecords from "../../../hooks/useAnomalyRecords";

const CreateLRUTReport: React.FC = () => {
    const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { isSaving } = useAppSelector(getReportDetails);
	const { fviCoverData } = useAppSelector(getCoverData);

	const [previewReport, setPreviewReport] = useState(false);
	const [coverData, setCoverData] = useState(fviCoverData);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
    const [dataItems, setDataItems] = useState<LRUTDataItem[]>([]);
    const [drawing, setDrawing] = useState<PDFImageProps[]>([]);
    const [executiveSummary, setExecutiveSummary] = useState('');
    const [generalInfo, setGeneralInfo] = useState<LRUTGeneralInformation>(initialGeneralInformation);
    const [pipingItems, setPipingItems] = useState<FVIPipingItem[]>([]);
    const [photoRecord, setPhotoRecord] = useState<PDFImageProps[]>([]);
    const [tmlLocations, setTMLLocations] = useState<PDFImageProps[]>([]);
	const [utInspectionOverview, setUTInspectionOverview] = useState(initialUTMOverview);
	const [utInspectionItems, setUTInspectionItems] = useState<UTMInspection[]>([]);

    const reportDetails = useMemo(() => {
        return {
            coverData,
            reportTitle,
            executiveSummary,
            drawing,
            dataItems,
            generalInfo,
            pipingItems,
            utInspectionItems,
            utInspectionOverview,
            tmlLocations,
            photoRecord,
            projectDetails,
        }
    }, [coverData, reportTitle, dataItems, drawing, executiveSummary, generalInfo, photoRecord, pipingItems, projectDetails, tmlLocations, utInspectionItems, utInspectionOverview]);

    const validationMessage = useMemo(() => {
        if (!validateWorkOrderNumber(reportDetails.coverData.workOrderNumber)) {
            return 'Enter a valid work order number!';
        }

        if (reportDetails.executiveSummary.length === 0) {
            return 'Please enter an executive summary!';
        }

        if (reportDetails.photoRecord.length === 0) {
            return 'Please provide at least one photo!';
        }

        if (reportDetails.drawing.length === 0) {
            return 'Please upload at least one drawing!';
        }

        return ''
    }, [reportDetails]);

    const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();
    const user = getCurrentUser() as User;

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

    const handleSaveAndPreview = useCallback((): void => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);

    useEffect(() => {
        function initializeCache() {
            const cachedReport = autoSave.oldReportData as typeof reportDetails;
            if (!cachedReport) {
                if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);

                return;
            }

            setCoverData(cachedReport.coverData);
            setReportTitle(cachedReport.reportTitle);
            setExecutiveSummary(cachedReport.executiveSummary);
            setDrawing(cachedReport.drawing);
            setDataItems(cachedReport.dataItems);
            setPipingItems(cachedReport.pipingItems);
            setGeneralInfo(cachedReport.generalInfo);
            setPhotoRecord(reArrangePhotoRecord(cachedReport.photoRecord));
            setUTInspectionItems(cachedReport.utInspectionItems)
            setUTInspectionOverview(cachedReport.utInspectionOverview);
            setTMLLocations(cachedReport.tmlLocations);
			
            dispatch(setLastPhotoNumber(cachedReport.photoRecord.length));
        }

        initializeCache();
    }, [autoSave.oldReportData, dispatch, fviCoverData]);

    useAnomalyRecords(pipingItems.flatMap((piping) => piping.anomalyItems));

    return ( 
        <Fragment>
            <Modal isVisible={previewReport} onClose={togglePreviewReport}>
                <CustomLRUTReport {...reportDetails} />
            </Modal>

            <div className="mb-4">
                <SyncTime lastSynced={autoSave.lastSaved} />
            </div>

            {projectDetails && (
                <FVICoverData
                    coverDataId={config.coverDataTemplates.lrut}
                    isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.lrut && user.role.approveReports}
                    subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} ${projectDetails.inspectionTypeName} Reports`}
                />
            )}

            <Conditional isVisible={validationMessage.length > 0}>
                <WarningNotice title='Incomplete Report' body={validationMessage} />
            </Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>

			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<ExecutiveSummaryForm initialSummary={executiveSummary} onProceed={setExecutiveSummary} />
			</Zippy>

			<Zippy headerTitle="General Information" isValid={!!generalInfo.equipmentSN}>
				<div className="p-4 rounded bg-light bordered mb-4">
                    <LRUTGeneralInformationForm initialLRUTData={generalInfo} onProceed={setGeneralInfo} />
				</div>
			</Zippy>

            <Zippy headerTitle="Drawing / Iso" isValid={drawing.length > 0}>
                <Drawing initialDrawing={drawing} onAdd={setDrawing} />
			</Zippy>

			<PipingItems pipingItems={pipingItems} onChangePipingItems={setPipingItems} />

            <Zippy headerTitle="Guided Wave Items" isValid={dataItems.length > 0}>
				<LRUTDataItems dataItems={dataItems} onDataChange={setDataItems} />
			</Zippy>

            <CustomUTMSection
                tmlSectionLabel="LRUT Collar & TML Location"
                tmlLocations={tmlLocations}
                onChangeTMLLocations={setTMLLocations}
                utmItems={utInspectionItems}
                utmOverview={utInspectionOverview}
                onItemsChange={setUTInspectionItems}
                onOverviewChange={setUTInspectionOverview}
            />

            <Zippy headerTitle="General Photos" isValid={photoRecord.length > 0}>
				<VIPhotoRecordForm initialGeneralPhotos={photoRecord} onProceed={setPhotoRecord} />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving }
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
        </Fragment>
     );
};

const initialGeneralInformation: LRUTGeneralInformation = {
	technologyIntroduction: 'Guide Wave Ultrasonic Test / Long Range Ultrasonic Test is provides rapid screening for corrosion and erosion in pipelines. Its fast  method for inspecting long lengths of pipe and an efficient technique used to test large volumes of material from a single test point.',
    material: '',
    fluidType: '',
    nominalThickness: 0,
    corrosionAllowance: 0,
    outerDiameter: 0,
    dateOfInspection: new Date(),
    calibrationDueDate: new Date(),
    guidedWaveTechnology: '',
    equipmentSN: '',
    cableCheck: '',
    collarSize: 0,
    collarSN: '',
};

const initialUTMOverview: Omit<CustomUTMOverview, 'data'> = {
	ndeTechnique: '',
	equipment: '',
	probe: '',
	amplitudeLinearityCheck: '',
	screenHeightLinearityCheck: '',
	calibrationBlk: '',
	refStd: '',
	serialNo: '',
	scanningLevel: '',
	couplant: '',
	remarks: '',
	material: '',
	calibrationDueDate: new Date(),
	dateOfUTM: null,
	corrosionAllowance: 0,
	unit: 'mm',
	summary: '',
    techniqueType: '',
	velocity: 0,
};

export default CreateLRUTReport;